import { Component, Event, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { generateRandomNumber } from '../../../../global/utils/utils';
import { AddressSearchService } from './address-search.service';
export class AddressSearchComponent {
  constructor() {
    /**
     * Address search api baseUrl
     */
    this.baseUrl = '';
    /**
     * Address search api subscriptionKey
     */
    this.subscriptionKey = '';
    /**
     * TO BE DEPRECATED:
     * Address search isMailingAddress flag. If true, will include poBox and unserviceable addresses
     */
    this.isMailingAddress = false;
    /**
     * Address search includeUnserviceable flag. If true, will include unserviceable addresses
     */
    this.includeUnserviceable = false;
    /**
     * Address search includePostOfficeBox flag. If true, will include poBox addresses
     */
    this.includePostOfficeBox = false;
    /**
     * The label text for the input, will be hidden if type is experiential
     */
    this.label = '';
    /**
     * The label id for the input, needs to be unique for accessibility standards.
     */
    this.labelId = generateRandomNumber();
    /**
     * Type for the control, either default, default-inverse or experiential
     */
    this.type = 'default';
    /**
     * The placeholder property for the input, only used if type is experiential
     */
    this.placeholder = '';
    /**
     * The address input value
     */
    this.addressInputValue = '';
    /**
     * Do address search and select first option when address is provided in addressInputValue Prop
     */
    this.addressInputAutoSelect = false;
    /**
     * The debounce time while user type in address
     */
    this.debounceTime = 600;
    /**
     * Manual address modal heading
     */
    this.manualAddressHeading = '';
    /**
     * Validation text to show when the has error flag is true
     */
    this.validationText = '';
    /**
     * Text to show when valid. Will override the hint text if present at the bottom of the component.
     */
    this.validText = '';
    /**
     * Shows the valid icon when validSelectionMode is set to external or onSelect and showValidIcon
     * equals true. validSelectionMode should be used when an api call regulates the visibility of the
     * icon ie when an address is searched for by the user and the address is servicable. onSelect should
     * be used with a static list of options (eg street Name) where any selection is valid
     * If set to false, valid state will never display.
     */
    this.validSelectionMode = 'off';
    /**
     * Hint text will be shown underneath the textbox but will be hidden if there is an error
     */
    this.hintText = '';
    /**
     * Flag to show error state
     */
    this.hasError = false;
    /**
     * Flag to show spinner
     */
    this.showSpinner = false;
    /**
     * Text that shows on the option that triggers manual address entry
     */
    this.manualAddressOptionText = "Can't find address? Enter it manually";
    /**
     * Highlight text that shows on the option that triggers manual address entry
     */
    this.manualAddressOptionHighlightText = 'Enter it manually';
    /**
     * Address options that shows in the suggestion list
     */
    this.options = [];
    /**
     * Address search state, idle/displayResults/loading/resultSelected
     */
    this.autoCompleteState = 'idle';
  }
  /**
   * Sets the textbox component element
   */
  async setTextBoxValue(textValue) {
    await this.autocompleteComp.setTextBoxValue(textValue);
  }
  componentWillLoad() {
    this.addressSearchService = new AddressSearchService(this.baseUrl, this.subscriptionKey, this.isMailingAddress, this.includeUnserviceable, this.includePostOfficeBox);
    if (this.addressInputValue) {
      this.setAddressValue(this.addressInputValue);
    }
  }
  async componentDidLoad() {
    this.manualAddressComp.setTriggerButton(() => {
      return this.autocompleteComp.querySelector('agl-ds-textbox');
    });
    if (this.addressInputAutoSelect) {
      await this.valueWatchHandler(this.addressInputValue);
    }
  }
  async handleAddressSearch(query) {
    const manualAddressOption = {
      value: 'manualAddressOption',
      text: this.manualAddressOptionText,
      doNotComplete: true,
      highlightText: this.manualAddressOptionHighlightText
    };
    if (query.length >= 7) {
      this.autoCompleteState = 'loading';
      try {
        const addressDataModal = await this.addressSearchService.getAddressList(query);
        this.options = [
          ...addressDataModal.data.map((x) => {
            return { value: x.id, text: x.address };
          }),
          manualAddressOption
        ];
      }
      catch (e) {
        this.options = [manualAddressOption];
      }
      this.autoCompleteState = 'displayResults';
    }
    else {
      this.options = [];
      this.autoCompleteState = 'idle';
    }
  }
  async handleClick() {
    const element = await this.autocompleteComp.getTextBoxElement();
    await element.selectInput();
  }
  debounce(callback, wait) {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(context, args), wait);
    };
  }
  /**
   * Sets the component value based off a previously stored address details model
   */
  setAddressString(addressDetailModel) {
    if (addressDetailModel) {
      if (addressDetailModel.poBoxType) {
        const poBoxNumber = addressDetailModel.poBoxNumber !== null && addressDetailModel.poBoxNumber !== undefined ? addressDetailModel.poBoxNumber : '';
        this.addressString = `${addressDetailModel.poBoxType} ${poBoxNumber}, ${addressDetailModel.suburb} ${addressDetailModel.state} ${addressDetailModel.postcode}`.trimLeft();
      }
      else {
        const unitNumber = addressDetailModel.unitNumber !== null && addressDetailModel.unitNumber !== undefined ? addressDetailModel.unitNumber : '';
        this.addressString = `${unitNumber} ${addressDetailModel.streetNumber} ${addressDetailModel.streetName} ${addressDetailModel.streetType}, ${addressDetailModel.suburb} ${addressDetailModel.state} ${addressDetailModel.postcode}`.trimLeft();
      }
    }
  }
  /**
   * Set the display value of the address search component based on a custom string or an address details model
   * @param value The address value to dsiplay as either a string or addressDetailModel
   */
  setAddressValue(value) {
    if (typeof value === 'string') {
      this.addressString = value;
    }
    else {
      this.setAddressString(value);
    }
  }
  async valueWatchHandler(newValue) {
    this.setAddressValue(newValue);
    if (this.addressInputAutoSelect && typeof newValue === 'string') {
      await this.handleAddressSearch(newValue);
      // last entry is always the 'manual entry' option
      if (this.options.length > 1) {
        // automatically select first address
        this.setAddressValue(this.options[0].text);
        const event = { detail: { value: this.options[0].value } };
        await this.handleOptionSelected(event);
      }
    }
  }
  async handleOptionSelected(event) {
    if (event.detail.value === 'manualAddressOption') {
      this.manualAddressOptionClicked.emit();
      this.manualAddressComp.openModal(this.autocompleteComp.querySelector('input'));
      this.autoCompleteState = 'resultSelected';
      return;
    }
    this.autoCompleteState = 'loading';
    const response = await this.addressSearchService.getAddressDetails(event.detail.value);
    this.addressSelected.emit(Object.assign(Object.assign({}, response.data.address), { latitude: response.data.geoLocation ? response.data.geoLocation.latitude : '', longitude: response.data.geoLocation ? response.data.geoLocation.longitude : '', poBoxNumber: response.data.postOfficeBox ? response.data.postOfficeBox.postOfficeBoxNumber : '', poBoxType: response.data.postOfficeBox ? response.data.postOfficeBox.postOfficeBoxType : null }));
    this.autoCompleteState = 'resultSelected';
  }
  render() {
    return (h("div", null,
      h("agl-ds-autocomplete", { autoCompleteId: this.labelId, value: this.addressString, label: this.label, type: this.type, placeholder: this.placeholder, options: this.options, parentState: this.showSpinner ? 'loading' : this.autoCompleteState, mode: "controlled", hintText: this.hintText, validationText: this.validationText, validText: this.validText, showValidIcon: this.showValidIcon, validSelectionMode: this.validSelectionMode, hasError: this.hasError, ref: (el) => {
          this.autocompleteComp = el;
        }, onAutocompleteInput: this.debounce((e) => {
          this.addressSearchInput.emit(e.detail);
          this.handleAddressSearch(e.detail.target.value);
        }, this.debounceTime), onClick: () => {
          this.handleClick();
        }, onAutocompleteBlur: () => {
          this.addressSearchBlur.emit();
        } }),
      h("agl-ds-manual-address-entry", { heading: this.manualAddressHeading, ref: (el) => {
          this.manualAddressComp = el;
        }, includePobox: this.isMailingAddress || this.includePostOfficeBox, onAddressConfirm: (e) => {
          this.addressSelected.emit(e.detail);
          this.setAddressString(e.detail);
        } })));
  }
  static get is() { return "agl-ds-address-search"; }
  static get properties() { return {
    "baseUrl": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Address search api baseUrl"
      },
      "attribute": "base-url",
      "reflect": false,
      "defaultValue": "''"
    },
    "subscriptionKey": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Address search api subscriptionKey"
      },
      "attribute": "subscription-key",
      "reflect": false,
      "defaultValue": "''"
    },
    "isMailingAddress": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "TO BE DEPRECATED:\nAddress search isMailingAddress flag. If true, will include poBox and unserviceable addresses"
      },
      "attribute": "is-mailing-address",
      "reflect": false,
      "defaultValue": "false"
    },
    "includeUnserviceable": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Address search includeUnserviceable flag. If true, will include unserviceable addresses"
      },
      "attribute": "include-unserviceable",
      "reflect": false,
      "defaultValue": "false"
    },
    "includePostOfficeBox": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Address search includePostOfficeBox flag. If true, will include poBox addresses"
      },
      "attribute": "include-post-office-box",
      "reflect": false,
      "defaultValue": "false"
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label text for the input, will be hidden if type is experiential"
      },
      "attribute": "label",
      "reflect": false,
      "defaultValue": "''"
    },
    "labelId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label id for the input, needs to be unique for accessibility standards."
      },
      "attribute": "label-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "TextboxType",
        "resolved": "\"default\" | \"default-inverse\" | \"experiential\"",
        "references": {
          "TextboxType": {
            "location": "import",
            "path": "../../../core/textbox/textbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Type for the control, either default, default-inverse or experiential"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "placeholder": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The placeholder property for the input, only used if type is experiential"
      },
      "attribute": "placeholder",
      "reflect": false,
      "defaultValue": "''"
    },
    "addressInputValue": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string | AddressDetailModel",
        "resolved": "AddressDetailModel | string",
        "references": {
          "AddressDetailModel": {
            "location": "import",
            "path": "../address.model"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The address input value"
      },
      "attribute": "address-input-value",
      "reflect": false,
      "defaultValue": "''"
    },
    "addressInputAutoSelect": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Do address search and select first option when address is provided in addressInputValue Prop"
      },
      "attribute": "address-input-auto-select",
      "reflect": false,
      "defaultValue": "false"
    },
    "debounceTime": {
      "type": "number",
      "mutable": false,
      "complexType": {
        "original": "number",
        "resolved": "number",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The debounce time while user type in address"
      },
      "attribute": "debounce-time",
      "reflect": false,
      "defaultValue": "600"
    },
    "manualAddressHeading": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Manual address modal heading"
      },
      "attribute": "manual-address-heading",
      "reflect": false,
      "defaultValue": "''"
    },
    "validationText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Validation text to show when the has error flag is true"
      },
      "attribute": "validation-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "validText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Text to show when valid. Will override the hint text if present at the bottom of the component."
      },
      "attribute": "valid-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "showValidIcon": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows the valid icon when it equals true and validSelectionMode is set to external or onSelect"
      },
      "attribute": "show-valid-icon",
      "reflect": false
    },
    "validSelectionMode": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "validSelectionMode",
        "resolved": "\"external\" | \"off\" | \"onSelect\"",
        "references": {
          "validSelectionMode": {
            "location": "import",
            "path": "../../autocomplete/autocomplete.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows the valid icon when validSelectionMode is set to external or onSelect and showValidIcon\nequals true. validSelectionMode should be used when an api call regulates the visibility of the\nicon ie when an address is searched for by the user and the address is servicable. onSelect should\nbe used with a static list of options (eg street Name) where any selection is valid\nIf set to false, valid state will never display."
      },
      "attribute": "valid-selection-mode",
      "reflect": false,
      "defaultValue": "'off'"
    },
    "hintText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Hint text will be shown underneath the textbox but will be hidden if there is an error"
      },
      "attribute": "hint-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "hasError": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show error state"
      },
      "attribute": "has-error",
      "reflect": false,
      "defaultValue": "false"
    },
    "showSpinner": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show spinner"
      },
      "attribute": "show-spinner",
      "reflect": false,
      "defaultValue": "false"
    },
    "manualAddressOptionText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Text that shows on the option that triggers manual address entry"
      },
      "attribute": "manual-address-option-text",
      "reflect": false,
      "defaultValue": "\"Can't find address? Enter it manually\""
    },
    "manualAddressOptionHighlightText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Highlight text that shows on the option that triggers manual address entry"
      },
      "attribute": "manual-address-option-highlight-text",
      "reflect": false,
      "defaultValue": "'Enter it manually'"
    }
  }; }
  static get states() { return {
    "options": {},
    "autoCompleteState": {},
    "addressString": {}
  }; }
  static get events() { return [{
      "method": "addressSelected",
      "name": "addressSelected",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when address option selected. AddressDetailModel will be emitted"
      },
      "complexType": {
        "original": "AddressDetailModel",
        "resolved": "AddressDetailModel",
        "references": {
          "AddressDetailModel": {
            "location": "import",
            "path": "../address.model"
          }
        }
      }
    }, {
      "method": "manualAddressOptionClicked",
      "name": "manualAddressOptionClicked",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when manual address option is selected"
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "addressSearchInput",
      "name": "addressSearchInput",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when user starts typing in the textbox for address search"
      },
      "complexType": {
        "original": "InputEvent",
        "resolved": "InputEvent",
        "references": {
          "InputEvent": {
            "location": "global"
          }
        }
      }
    }, {
      "method": "addressSearchBlur",
      "name": "addressSearchBlur",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when text input loses focus"
      },
      "complexType": {
        "original": "FocusEvent",
        "resolved": "FocusEvent",
        "references": {
          "FocusEvent": {
            "location": "global"
          }
        }
      }
    }]; }
  static get methods() { return {
    "setTextBoxValue": {
      "complexType": {
        "signature": "(textValue: string) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Sets the textbox component element",
        "tags": []
      }
    }
  }; }
  static get watchers() { return [{
      "propName": "addressInputValue",
      "methodName": "valueWatchHandler"
    }]; }
  static get listeners() { return [{
      "name": "autocompleteOptionSelected",
      "method": "handleOptionSelected",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
